import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CaretDown, Check, Pencil, Plus, Trash } from "@phosphor-icons/react";
import { Fragment, useEffect, useState } from "react";

import { format, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import Paywall from "@/components/Paywall";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useFundingType } from "@/hooks/useFundingType";
import { cn } from "@/lib/utils";
import { addFundingRound, updateFundingRound } from "@/services/api/Startup";
import {
  createSimpleInvestor,
  searchInvestorSimpleAll,
} from "@/services/api/VentureCapital";
import { Combobox, Transition } from "@headlessui/react";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import Datepicker from "react-tailwindcss-datepicker";

export const EditFundingRound = ({
  startup,
  fundingRound,
  isTable,
  planSubscription,
  user,
  hasAccess,
  action,
}) => {
  const [date, setDate] = useState({
    startDate: null,
    endDate: null
  });
  const [selected, setSelected] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [query, setQuery] = useState("");
  const [investors, setInvestors] = useState([]);
  const { data: fundingTypes } = useFundingType();
  const [selectedFunding, setSelectedFunding] = useState(null);
  const [roundAmount, setRoundAmount] = useState(null);
  const [isUndisclosed, setIsUndisclosed] = useState(false);
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [isAmountUndisclosed, setIsAmountUndisclosed] = useState(false);

  const [value, setValue] = useState({
    startDate: null,
    endDate: null,
  });
  const changeDatepickerValue = (newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (selected.length > 0) {
      setQuery("");
    }
  }, [selected]);

  const handleSearch = async (e) => {
    const value = e.target.value;
    setQuery(value);
    if (value) {
      const response = await searchInvestorSimpleAll({
        params: {
          keyword: value,
        },
      });
      setInvestors(response.data.data.map((d) => ({
        ...d.searchable,
        type: d.type
      })));
    } else {
      setInvestors([]);
    }
  };

  const onClearSearch = () => {
    setQuery("");
    setInvestors([]);
  };

  const onRemoveSelected = (s) => {
    const filtered = selected.filter((d) => d.id != s.id);
    setSelected(filtered);
  };

  const handleSubmit = async () => {
    if (!selectedFunding) return toast.error("Funding Round is required!");
    if (!isAmountUndisclosed && !roundAmount) return toast.error("Round Amount is required!");
    if (!date) return toast.error("Announced Date is required!");

    // Separate selected items by type
    const ventureCapitals = selected.filter(s => s.type === 'venture_capitals');
    const investorLabels = selected.filter(s => s.type === 'investor_labels');

    const payload = {
      funding_type_id: selectedFunding,
      round_amount: isAmountUndisclosed ? 0 : (roundAmount ? Number(String(roundAmount).replace(/,/g, '')) : 0),
      round_currency: "USD",
      date_announced: dayjs(date.startDate).format("YYYY-MM-DD 00:00:00"),
      startup_id: startup.id,
      investors: ventureCapitals.map((s) => s.id),
      investor_labels: investorLabels.map((s) => s.id),
      amount_undisclosed: isAmountUndisclosed ? 1 : 0,
    };

    try {
      if (fundingRound) {
        await updateFundingRound(fundingRound.id, payload);
      } else {
        await addFundingRound(payload);
      }
      toast.success("Successfully add funding round!");
      setTimeout(() => {
        if (action) {
          action();
          setOpen(false);
        } else {
          router.reload();
        }
      }, 200);
    } catch (error) {}
  };

  useEffect(() => {
    if (fundingRound) {
      setRoundAmount(fundingRound.round_amount);
      setDate({
        startDate: new Date(fundingRound.date_announced),
        endDate: new Date(fundingRound.date_announced)
      });
      setSelectedFunding(fundingRound.funding_type_id);
      // Combine investors and investor_labels with their types
      const combinedSelected = [
        ...(fundingRound.investors || []).map(investor => ({
          ...investor,
          type: 'venture_capitals'
        })),
        ...(fundingRound.investor_labels || []).map(label => ({
          ...label,
          type: 'investor_labels'
        }))
      ];
      setSelected(combinedSelected);
      setIsAmountUndisclosed(fundingRound.amount_undisclosed === 1);
    }
  }, [fundingRound]);

  useEffect(() => {
    if (isUndisclosed) {
      setSelected([]);
    }
  }, [isUndisclosed]);

  const createSimpleVC = async () => {
    const response = await createSimpleInvestor({ name: query });
    setSelected((state) => [...state, {
      ...response.data.data,
      type: 'venture_capitals'
    }]);
  };

  if (
    !(
      (planSubscription && planSubscription.is_startup_plan) ||
      user?.roles == "admin-team" ||
      user?.roles == "admin" ||
      hasAccess
    )
  ) {
    return (
      <Paywall
        title={
          "Upgrade to the Startup Registration plan to edit your startup profile"
        }
        description={`Only users with the Startup Registration plan can edit and publish the startup profile.`}
        startup={startup}
      >
        <Button
          size='icon'
          className='relative w-10 h-10 text-xl text-white rounded-full bg-gradient-to-tr from-yellow-1 to-red-1'
        >
          <span className='absolute inline-flex w-full h-full rounded-full animate-ping bg-orange-1/50'></span>
          {fundingRound ? <Pencil weight='fill' /> : <Plus />}
        </Button>
      </Paywall>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isTable ? (
          <Button size='sm' className='h-auto px-2 py-1.5 text-xs'>
            <Pencil className='w-4 h-4' />
            {/* Edit */}
          </Button>
        ) : (
          <Button
            size='icon'
            className='relative w-10 h-10 text-xl text-white rounded-full bg-gradient-to-tr from-yellow-1 to-red-1'
          >
            <span className='absolute inline-flex w-full h-full rounded-full animate-ping bg-orange-1/50'></span>

            {fundingRound ? <Pencil weight='fill' /> : <Plus />}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className='max-w-2xl bg-white text-background'>
        <DialogHeader>
          <DialogTitle>
            {fundingRound ? "Edit" : "Add"} Funding Round
          </DialogTitle>
          <DialogDescription>
            <div className='grid flex-1 grid-cols-12 mt-5 text-left gap-x-3 gap-y-4'>
              <div className='w-full col-span-6 lg:col-span-4 space-y-1.5'>
                <Label className='text-xs text-background'>Funding Round</Label>
                <Select
                  id='established'
                  onValueChange={(value) => setSelectedFunding(value)}
                  value={selectedFunding}
                >
                  <SelectTrigger className='bg-white text-background border-background/20 focus:ring-0'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='bg-white text-background border-background/20'>
                    <ScrollArea className='h-60'>
                      {fundingTypes.map((f) => (
                        <SelectItem
                          value={f.id}
                          key={`${f.id}`}
                          className='data-[highlighted]:text-white'
                        >
                          {f.name}
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
                {/* <select
                  className='block w-full h-10 px-4 text-white border rounded-md bg-white/5 border-background/20 focus:border-orange-1 focus:ring-transparent sm:text-sm'
                  value={selectedFunding}
                  onChange={(e) => setSelectedFunding(e.target.value)}
                >
                  <option></option>
                  {fundingTypes.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select> */}
              </div>
              <div className='w-full col-span-6 lg:col-span-4 space-y-1.5'>
                <Label
                  className='text-xs text-background'
                  htmlFor='headquarter'
                >
                  Announced Date
                </Label>
                <Datepicker
                  useRange={false}
                  asSingle={true}
                  value={date}
                  onChange={setDate}
                  inputClassName='bg-white border w-full rounded-md h-10 px-3 ring-offset-white border-background/20 text-sm'
                />
                {/* <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "bg-white hover:bg-white w-full text-background justify-start text-left font-normal border-background/20",
                        !date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className='w-4 h-4 mr-2' />
                      {date && isValid(new Date(date)) ? (
                        format(new Date(date), "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-auto p-0'>
                    <Calendar
                      mode='single'
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover> */}
              </div>
              <div className='w-full col-span-12 lg:col-span-4 space-y-1.5'>
                <Label className='text-xs text-background'>
                  Round Amount
                  <span className="ml-1 text-[10px] text-gray-500">(numbers only)</span>
                </Label>
                <div className='relative h-10'>
                  <Input
                    type="text"
                    className='absolute top-0 text-right bg-white pr-11 border-background/20 focus-visible:ring-0 text-background'
                    value={isAmountUndisclosed ? '' : roundAmount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d]/g, '');
                      const formatted = value ? Number(value).toLocaleString('en-US') : '';
                      setRoundAmount(formatted);
                    }}
                    onKeyPress={(e) => {
                      if (!/[\d]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                        e.preventDefault();
                      }
                    }}
                    disabled={isAmountUndisclosed}
                  />
                  <span className='absolute text-sm font-medium -translate-y-1/2 right-2 opacity-70 top-1/2'>
                    USD
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="amountUndisclosed"
                    checked={isAmountUndisclosed}
                    onChange={(e) => setIsAmountUndisclosed(e.target.checked)}
                    className="rounded border-background/20"
                  />
                  <Label htmlFor="amountUndisclosed" className="text-xs text-background cursor-pointer">
                    Undisclosed Amount
                  </Label>
                </div>
              </div>
              <div className='col-span-12'>
                <button
                  onClick={() => setIsUndisclosed((s) => !s)}
                  className={`float-right text-black text-xs rounded px-2 py-1 ${
                    isUndisclosed
                      ? "bg-slate-600 text-white"
                      : "border opacity-50"
                  } mb-2`}
                >
                  Undisclosed
                </button>
                <div className='flex-1 space-y-1.5'>
                  <Label className='text-xs text-background'>Investor</Label>
                  {!isUndisclosed && (
                    <Combobox value={selected} onChange={setSelected} multiple>
                      <div className='relative'>
                        <div className=''>
                          <Combobox.Input
                            className='w-full py-2.5 pl-3 text-background pr-10 bg-white border-0 rounded-md shadow-sm ring-1 ring-inset ring-background/20 focus:ring-0 focus:ring-inset focus:ring-background sm:text-sm'
                            onChange={handleSearch}
                            placeholder='Type to search'
                            value={query}
                          />
                          <Combobox.Button className='absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none'>
                            <CaretDown aria-hidden='true' />
                          </Combobox.Button>
                        </div>
                        {query ? (
                          <Transition
                            as={Fragment}
                            leave='transition ease-in duration-100'
                            leaveFrom='opacity-100'
                            leaveTo='opacity-0'
                            afterLeave={() => setQuery("")}
                          >
                            <Combobox.Options className='absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
                              {investors.length === 0 && query !== "" ? (
                                <div className='relative px-4 py-2 text-gray-700 cursor-default select-none'>
                                  Nothing found
                                </div>
                              ) : (
                                investors.map((investor) => (
                                  <Combobox.Option
                                    disabled={selected.find(
                                      (s) => s.id == investor.id
                                    )}
                                    key={investor?.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                        active
                                          ? "bg-background text-white"
                                          : "text-background"
                                      }`
                                    }
                                    value={investor}
                                  >
                                    {({ selected: added, active }) => (
                                      <>
                                        <span
                                          className={`block truncate ${
                                            added
                                              ? "font-medium"
                                              : "font-normal"
                                          }`}
                                        >
                                          {investor?.name}
                                          {investor?.type === 'investor_labels' && (
                                            <span className="ml-2 text-xs text-gray-500">(label)</span>
                                          )}
                                        </span>
                                        {selected.find(
                                          (s) => s.id == investor.id
                                        ) && (
                                          <Check className='absolute left-3 bottom-2.5' />
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                              )}
                              <div
                                className='px-4 py-2 mt-2 bg-white rounded cursor-pointer'
                                onClick={createSimpleVC}
                              >
                                Create & Add{" "}
                                <span className='font-bold text-background'>
                                  {query}
                                </span>
                              </div>
                            </Combobox.Options>
                          </Transition>
                        ) : (
                          query !== "" &&
                          query.length > 2 && (
                            <Transition
                              as={Fragment}
                              leave='transition ease-in duration-100'
                              leaveFrom='opacity-100'
                              leaveTo='opacity-0'
                              afterLeave={() => setQuery("")}
                            >
                              <div
                                className='p-2 mt-2 bg-white rounded cursor-pointer'
                                onClick={createSimpleVC}
                              >
                                Create & Add{" "}
                                <span className='font-bold'>{query}</span>
                              </div>
                            </Transition>
                          )
                        )}
                      </div>
                    </Combobox>
                  )}
                </div>

                {!isUndisclosed && (
                  <div className='flex flex-col pl-3 mt-4 mb-4 ml-2 border-l gap-y-2 border-background/10'>
                    {selected.map((s) => (
                      <div
                        key={s.id}
                        className='flex items-center justify-between gap-2'
                      >
                        <span className='w-full px-4 py-2 font-medium rounded-md bg-background/5 text-background'>
                          {s.name}
                          {s.type === 'investor_labels' && (
                            <span className="ml-2 text-xs text-gray-500">(label)</span>
                          )}
                        </span>
                        <Button
                          onClick={() => onRemoveSelected(s)}
                          variant='link'
                          size='icon'
                        >
                          <Trash weight='bold' className='text-red-500' />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {/* <Button variant='link' className='text-sm pl-9'>
                  <Plus className='mr-2 text-base' /> Add Investor
                </Button> */}
              </div>
              <Button
                onClick={handleSubmit}
                size={"lg"}
                variant='secondary'
                className='col-span-12 font-medium'
              >
                {fundingRound ? "Save Changes" : "Add Funding Round"}
              </Button>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
